import { AddOnTypes } from '../../shared/constants/TenantFields';

const withUniqueBadges = (badges = []) => {
  const seen = new Set();
  const result = [];

  badges.forEach((badge) => {
    if (!badge || typeof badge.label !== 'string' || !badge.label.trim()) {
      return;
    }

    const label = badge.label.trim();
    const variant =
      typeof badge.variant === 'string' && badge.variant.trim() ? badge.variant.trim().toLowerCase() : 'neutral';
    const tooltip = typeof badge.tooltip === 'string' && badge.tooltip.trim() ? badge.tooltip.trim() : `${label} badge`;

    const key = `${label.toLowerCase()}|${variant}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push({ label, variant, tooltip });
    }
  });

  return result;
};

const badge = (label, variant, tooltip) => ({ label, variant, tooltip });
const normalizeQueryKey = (queryKey) => (typeof queryKey === 'string' ? queryKey.trim().toLowerCase() : '');

const TOOLTIP_PROACTIVE = 'Includes entries with at least one Proactive Type of Work.';
const TOOLTIP_REACTIVE = 'Includes entries with at least one Reactive Type of Work.';
const TOOLTIP_EXCLUSIVE = 'Entries tagged with both Proactive and Reactive Types of Work are excluded.';
const TOOLTIP_YEARLY = 'Past years are full-year totals; current year is year-to-date.';
const TOOLTIP_CAMPAIGN_REQUIRED = 'Entries without a campaign are excluded.';
const TOOLTIP_PERCENT = 'Displayed as a monthly percentage of the total.';
const TOOLTIP_NA_INCLUDED = 'Missing values appear as N/A.';

const capRuleBadges = (badges, queryKey) => {
  const normalizedQueryKey = normalizeQueryKey(queryKey);
  const labels = new Set(badges.map((badgeItem) => badgeItem.label.toLowerCase()));
  const allowsThreeForProactiveExclusion =
    normalizedQueryKey === 'proactive-media-relations-tactics' &&
    labels.has('proactive') &&
    labels.has('exclusive') &&
    labels.has('excludes');
  const allowsThreeForYearlyCampaignNa =
    labels.has('yearly') && labels.has('campaign required') && labels.has('n/a included');
  const maxBadges = allowsThreeForProactiveExclusion || allowsThreeForYearlyCampaignNa ? 3 : 2;
  if (badges.length <= maxBadges) {
    return badges;
  }
  if (process.env.NODE_ENV !== 'production') {
    console.warn(`Capping team analytics badges for ${queryKey} to ${maxBadges}.`);
  }
  return badges.slice(0, maxBadges);
};

export const getBadgesForTeamAnalytics = (queryKey, options = {}) => {
  const key = normalizeQueryKey(queryKey);
  const isEngagementAddOn = options?.addOn === AddOnTypes.engagementLog; // retained for safety checks

  const map = {
    'total-media-interactions': [],
    'team-driven-media-interactions': [badge('Percent', 'neutral', TOOLTIP_PERCENT)],
    'proactive-client-media-interactions': [
      badge('Proactive', 'proactive', TOOLTIP_PROACTIVE),
      badge('Exclusive', 'neutral', TOOLTIP_EXCLUSIVE),
    ],
    'institutional-story-media-interactions': [],
    'issue-response-media-interactions': [
      badge('Issue-only', 'issue', 'Includes interactions tagged Issue or “Issue Response”.'),
    ],
    'issues-complexity': [badge('Issue-only', 'issue', 'Includes services tagged as Issue.')],
    'tone-of-interactions': [
      badge('Yearly', 'neutral', TOOLTIP_YEARLY),
      badge('N/A included', 'neutral', TOOLTIP_NA_INCLUDED),
    ],
    'proactive-media-relations-tactics': [
      badge('Proactive', 'proactive', TOOLTIP_PROACTIVE),
      badge('Exclusive', 'neutral', TOOLTIP_EXCLUSIVE),
      badge('Excludes', 'neutral', 'Excludes services tagged “Institutional Story”.'),
    ],
    'proactive-services': [
      badge('Proactive', 'proactive', TOOLTIP_PROACTIVE),
      badge('Exclusive', 'neutral', TOOLTIP_EXCLUSIVE),
    ],
    'total-reactive-services': [
      badge('Reactive', 'reactive', TOOLTIP_REACTIVE),
      badge('Exclusive', 'neutral', TOOLTIP_EXCLUSIVE),
    ],
    'media-interactions-by-campaign': [
      badge('Yearly', 'neutral', TOOLTIP_YEARLY),
      badge('Campaign required', 'neutral', TOOLTIP_CAMPAIGN_REQUIRED),
    ],
    'services-by-campaign': [
      badge('Yearly', 'neutral', TOOLTIP_YEARLY),
      badge('Campaign required', 'neutral', TOOLTIP_CAMPAIGN_REQUIRED),
    ],
    'engagements-by-campaign': [
      badge('Yearly', 'neutral', TOOLTIP_YEARLY),
      badge('Campaign required', 'neutral', TOOLTIP_CAMPAIGN_REQUIRED),
    ],
    'total-engagements': [],
    'total-community-members-engaged': [],
  };

  const badges = map[key] || [];
  const normalized = withUniqueBadges(badges);
  const capped = capRuleBadges(normalized, queryKey);
  if (
    process.env.NODE_ENV !== 'production' &&
    isEngagementAddOn &&
    capped.some((badgeItem) => badgeItem.label === 'Engagement Log')
  ) {
    console.warn(`Descriptive Engagement Log badge still present for team analytics chart: ${queryKey}`);
  }
  return capped;
};
